import { motion } from "framer-motion";
import { CalendarDays, PartyPopper } from "lucide-react";
import { SCHOOL_HOLIDAYS, SPECIAL_SCHOOL_HOLIDAYS, PUBLIC_HOLIDAYS } from "@/lib/site";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const fmtIso = (iso) => `${Number(iso.slice(8, 10))} ${MONTHS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;

const seasonOf = ([start]) => {
  const m = Number(start.slice(5, 7));
  return m === 3 || m === 4 ? "Autumn" : m === 6 || m === 7 ? "Winter" : m === 9 || m === 10 ? "Spring" : "Summer";
};

const PH_NAMES = {
  "01-01": "New Year's Day",
  "03-21": "Human Rights Day",
  "03-26": "Good Friday",
  "03-29": "Family Day",
  "04-27": "Freedom Day",
  "05-01": "Workers' Day",
  "06-16": "Youth Day",
  "08-09": "National Women's Day",
  "09-24": "Heritage Day",
  "12-16": "Day of Reconciliation",
  "12-25": "Christmas Day",
  "12-26": "Day of Goodwill",
};

const phName = (iso) => {
  if (PH_NAMES[iso.slice(5)]) return PH_NAMES[iso.slice(5)];
  // a public holiday falling on a Sunday is observed on the Monday
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  const prev = `${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  return PH_NAMES[prev] ? `${PH_NAMES[prev]} (observed)` : "Public holiday";
};

export default function HolidayDates() {
  const today = new Date().toISOString().slice(0, 10);
  const breaks = SCHOOL_HOLIDAYS.filter(([, end]) => end >= today);
  const special = [...SPECIAL_SCHOOL_HOLIDAYS].filter((d) => d >= today).sort();
  const publics = [...PUBLIC_HOLIDAYS].filter((d) => d >= today).sort();

  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20" data-testid="holiday-dates-section">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.7 }}>
        <p className="text-xs font-black uppercase tracking-[0.3em] text-[#b98a2e]">Plan around the school calendar</p>
        <h2 className="mt-3 max-w-3xl text-4xl leading-tight text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)] sm:text-5xl" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>
          SCHOOL &amp; PUBLIC HOLIDAY DATES
        </h2>
        <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-[#5f5233]">
          On these dates — and every weekend — the park runs on the full day, 09h00 to 18h00. Entry is free; we're closed on 25 December and when it rains.
        </p>
      </motion.div>

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl border-2 border-[#d63a2f]/70 bg-gradient-to-br from-[#b91c1c] via-[#d63a2f] to-[#7f1d1d] p-8 shadow-[0_18px_45px_rgba(185,28,28,0.35)] sm:p-10"
          data-testid="holiday-school-card"
        >
          <span className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#ffd23f]/20" />
          <span className="pointer-events-none absolute -left-12 bottom-[-3rem] h-44 w-44 rounded-full bg-[#4a8ed9]/35" />
          <h3 className="flex items-center gap-3 text-3xl leading-tight text-[#ffd23f]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>
            <CalendarDays size={26} className="shrink-0 text-[#8ec3f5]" /> SCHOOL HOLIDAYS
          </h3>
          <ul className="mt-6 space-y-3">
            {breaks.map(([start, end]) => (
              <li
                key={start}
                className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-white/25 bg-white/15 px-5 py-3.5 transition-all hover:scale-[1.02] hover:border-[#8ec3f5]/70"
                data-testid={`holiday-break-${start}`}
              >
                <span className="text-base font-extrabold text-white">{seasonOf([start])} holidays</span>
                <span className="text-sm font-black text-[#8ec3f5]">
                  {fmtIso(start)} – {fmtIso(end)}
                </span>
              </li>
            ))}
          </ul>
          {special.length > 0 && (
            <p className="mt-5 rounded-2xl bg-[#7f1d1d]/70 px-4 py-3 text-xs font-bold text-[#ffd23f]">
              Plus special school holiday{special.length > 1 ? "s" : ""}: {special.map(fmtIso).join(", ")} — holiday times apply.
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative overflow-hidden rounded-3xl border-2 border-[#d63a2f]/50 bg-[#14120c] p-8 shadow-[0_18px_45px_rgba(36,28,13,0.35)] sm:p-10"
          data-testid="holiday-public-card"
        >
          <span className="pointer-events-none absolute -left-12 -bottom-12 h-44 w-44 rounded-full bg-[#d63a2f]/20" />
          <h3 className="flex items-center gap-3 text-3xl leading-tight text-[#f8b818]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>
            <PartyPopper size={26} className="shrink-0 text-[#ff6b5e]" /> PUBLIC HOLIDAYS
          </h3>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {publics.map((d) => (
              <li
                key={d}
                className="rounded-2xl border border-[#d63a2f]/35 bg-[#d63a2f]/15 px-5 py-3.5 transition-transform hover:scale-[1.03]"
                data-testid={`holiday-public-${d}`}
              >
                <span className="block text-sm font-black text-[#ff8a7a]">{fmtIso(d)}</span>
                <span className="mt-0.5 block text-sm font-extrabold text-[#fff7e8]">{phName(d)}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}